import { Injectable, signal, computed } from "@angular/core";
import { Area, AssertionIssue, db, InspectionJob, JobIssue, JobIssueVendor, Lot, Media, UserVendorContact, Zone } from "../../../../../core/database/db";
import { VendorDTO } from "../../../../../domains/vendors/models/VendorDTO";
import { JobIssueService } from "../../../../../domains/jobIssues/services/job-issue-service";
import { AssertionIssuesService } from "../../../../../domains/assertionIssues/services/assertion-issues.service";
import { VendorDataHelperService } from "../../../../../domains/vendors/services/vendor-data-helper.service";
import { UpsertContactResult, UserVendorContactService } from "../../../../../domains/vendors/services/user-vendor-contact.service";
import { LoggerService } from "../../../../../shared/services/logger.service";

@Injectable()
export class OpenItemsDataService {
  constructor(
    private jobIssueService: JobIssueService,
    private assertionIssueService: AssertionIssuesService,
    private vendorDataHelperService: VendorDataHelperService,
    private userVendorContactService: UserVendorContactService,
    private logger: LoggerService
  ) { }

  readonly lot = signal<Lot | undefined>(undefined);
  readonly jobs = signal<InspectionJob[] | undefined>(undefined);
  readonly zones = signal<Zone[] | undefined>(undefined);
  readonly areas = signal<Area[] | undefined>(undefined);
  readonly media = signal<Media[] | undefined>(undefined);
  readonly actionableJobIssues = signal<JobIssue[] | undefined>(undefined);
  readonly allJobIssuesForLot = signal<JobIssue[] | undefined>(undefined);
  readonly assertionIssues = signal<AssertionIssue[] | undefined>(undefined);
  readonly jobIssueVendors = signal<JobIssueVendor[] | undefined>(undefined);
  readonly vendors = signal<VendorDTO[] | undefined>(undefined);
  readonly userVendorContacts = signal<UserVendorContact[] | undefined>(undefined);
  readonly financialCommunityId = signal<number | null>(null);
  readonly isLoadingJobIssues = signal<boolean>(false);
  readonly isRefreshingInBackground = signal<boolean>(false);
  /** Set only when a background refresh finishes replacing already-visible data, so the
   *  fade-in effect never fires on the very first (cache) render. */
  readonly dataUpdatedPulse = signal<boolean>(false);

  readonly hasData = computed(() => {
    return !!this.lot() && !!this.jobs();
  });

  async updateIssue(
    existingJobIssue: JobIssue,
    updatedFields: Partial<JobIssue & AssertionIssue>,
    media: Media[],
  ): Promise<void> {
    const correspondingAssertionIssue = this.assertionIssues()?.find(ai => ai.identifier === existingJobIssue.identifier);

    await db.transaction("rw", [db.jobIssue, db.assertionIssue, db.media, db.syncStatus], async () => {
      await this.jobIssueService.updateJobIssue(existingJobIssue.id!, updatedFields, media);
      if (correspondingAssertionIssue) {
        await this.assertionIssueService.updateAssertionIssue(correspondingAssertionIssue.id!, updatedFields, media);
      }
    });

    const updatedJobIssue: JobIssue = {
      ...existingJobIssue,
      ...updatedFields,
      updatedAt: new Date()
    };

    const updatedAssertionIssue: AssertionIssue | undefined = correspondingAssertionIssue ? {
      ...correspondingAssertionIssue,
      ...updatedFields,
      updatedAt: new Date()
    } : undefined;

    this.actionableJobIssues.update(jobIssues => {
      if (!jobIssues) {
        return jobIssues;
      }

      return jobIssues
        .map(i => (i.identifier === existingJobIssue.identifier ? updatedJobIssue : i))
        .filter(i => !!i.isComplete === false && !!i.deleted === false);
    });

    if (updatedAssertionIssue) {
      this.assertionIssues.update(assertionIssues => {
        if (!assertionIssues) {
          return assertionIssues;
        }

        return assertionIssues.map(i => (i.identifier === updatedAssertionIssue.identifier ? updatedAssertionIssue : i));
      });
    }
  }

  async addNewIssue(newJobIssue: JobIssue) {
    this.actionableJobIssues.update(jobIssues => {
      if (!jobIssues) {
        return [newJobIssue];
      }

      return [...jobIssues, newJobIssue];
    });
  }

  /** Assigns a job issue to a trade partner or to the CM ("Assign to Me"), replacing the previous assignment. */
  addOrReplaceVendorAssignmentForIssue(
    issue: JobIssue,
    selectedVendor: VendorDTO | null,
    previousSelectedVendorAssignmentToReplace: JobIssueVendor | null
  ): Promise<JobIssueVendor | null> {
    return this.vendorDataHelperService.addOrReplaceVendorAssignmentForIssue(
      issue,
      selectedVendor,
      previousSelectedVendorAssignmentToReplace,
      this.jobIssueVendors,
      this.actionableJobIssues
    );
  }

  /** Removes a single assignee (the CM's self-assignment or one trade partner) from a job issue. */
  removeVendorFromIssue(issue: JobIssue, vendorToRemove: JobIssueVendor): Promise<void> {
    return this.vendorDataHelperService.removeVendorFromIssue(issue, vendorToRemove, this.jobIssueVendors, this.actionableJobIssues);
  }

  async addContactToVendor(contact: Omit<UserVendorContact, "id" | "deleted" | "updatedAt" | "syncStatus">) {
    const upsertResult = await this.userVendorContactService.upsertContact(contact as UserVendorContact);

    return this.processUpsertResult(upsertResult);
  }

  async updateContactForVendor(existingContact: UserVendorContact, contactUpdates: Pick<UserVendorContact, "email">) {
    const upsertResult = await this.userVendorContactService.upsertContact(existingContact, contactUpdates);

    if (upsertResult.upsertResult === "restored" || upsertResult.upsertResult === "updated") {
      await this.deleteContactFromVendor(existingContact.id!);
    }

    return this.processUpsertResult(upsertResult);
  }

  async deleteContactFromVendor(contactId: number): Promise<void> {
    await this.userVendorContactService.deleteContact(contactId);

    this.userVendorContacts.update(contacts => {
      if (!contacts) {
        return contacts;
      }
      return contacts.filter(c => c.id !== contactId);
    });
  }

  async bulkAssignVendorToIssues(issues: JobIssue[], vendor: VendorDTO, dueDate?: Date) {
    const assignments = await db.transaction("rw", [db.jobIssueVendor, db.jobIssue, db.userVendorContact, db.media, db.syncStatus], async () => {
      const assignments: JobIssueVendor[] = [];
      for (const issue of issues) {
        const assignment = await this.addOrReplaceVendorAssignmentForIssue(issue, vendor, null);
        if (assignment) {
          assignments.push(assignment);
        }
      }
      return assignments;
    });

    if (dueDate) {
      await Promise.all(issues.map(issue => this.updateIssue(issue, { dueDate }, [])));
    }

    return assignments;
  }

  async sendIssuesToVendor(issues: JobIssue[], vendorAssignments: JobIssueVendor[], vendor: VendorDTO, contacts: UserVendorContact[]): Promise<boolean> {
    try {
      const lotId = this.lot()!.id;

      const updatedVendorAssignments = await this.jobIssueService.sendIssuesToVendors(lotId, vendor.id, issues, vendorAssignments, contacts);

      this.jobIssueVendors.update(vendors => {
        return vendors?.map(v => {
          const updatedAssignment = updatedVendorAssignments.find(a => a.id === v.id);
          return updatedAssignment ? { ...v, ...updatedAssignment } : v;
        });
      });

      return updatedVendorAssignments.length > 0;
    } catch (error) {
      this.logger.error("Failed to send issues to vendors:", error);
      return false;
    }
  }

  private processUpsertResult(upsertResult: UpsertContactResult) {
    this.userVendorContacts.update(contacts => {
      if (!contacts) {
        return [upsertResult.contact];
      }

      return [...contacts, upsertResult.contact];
    });

    return upsertResult.contact;
  }
}